import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import type {
  Challenge,
  VerificationPurpose,
  VerifiedResult,
} from '@ecosconnect/core/src/auth/types';
import {
  createVerificationController,
  VerificationHost,
} from '@ecosconnect/core/src/auth/client';
import { authClient } from '@/services/auth';

type Options<P extends VerificationPurpose> = {
  purpose: P;
  challenge: Challenge;
  onVerified: (result: VerifiedResult[P]) => unknown;
  onExit: (message: string) => void;
};

const useVerificationChallenge = <P extends VerificationPurpose>(
  options: Options<P>
) => {
  const host = useRef<VerificationHost<P>>({
    ...options,
    requests: authClient,
  });
  useLayoutEffect(() => {
    host.current = { ...options, requests: authClient };
  });
  const [controller] = useState(() =>
    createVerificationController(
      options.purpose,
      options.challenge,
      () => host.current
    )
  );
  const snapshot = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot
  );
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (snapshot.focusCode) input.current?.focus();
  }, [snapshot.focusCode]);

  return {
    ...snapshot,
    input,
    setCode: controller.setCode,
    verify: controller.verify,
    resend: controller.resend,
    switchMethod: controller.switchMethod,
  };
};

export type VerificationChallenge = ReturnType<typeof useVerificationChallenge>;

export default useVerificationChallenge;
